import {Modal, ModalClose, ModalDialog, Stack, Typography} from "@mui/joy"
import {FullLogoSmall} from "./Logo"
import AuthForm, {AuthMode} from "./AuthForm"
import {useTranslation} from "react-i18next"

export type AuthDialogProps = {
    open: boolean
    /** Why the sign in was asked for, shown under the logo */
    reason?: string
    initialMode?: AuthMode
    onClose: () => void
}

/**
 * Sign in without leaving the page.
 *
 * The account is what a screen was missing, not a destination, so asking for it in place keeps the
 * page underneath and lets it carry on rendering the moment the session lands.
 */
const AuthDialog = ({open, reason, initialMode, onClose}: AuthDialogProps) => {
    const {t} = useTranslation()

    return <Modal open={open} onClose={onClose}>
        <ModalDialog layout={"center"}
                     sx={{
                         width: "100%",
                         maxWidth: "min(27rem, calc(100vw - 24px))",
                         maxHeight: "92dvh",
                         overflowY: "auto",
                         gap: 2,
                         padding: {xs: 2, sm: 3},
                         borderRadius: 15,
                         backgroundColor: "#0a1626",
                         borderColor: "rgba(255,255,255,0.1)"
                     }}>
            <ModalClose aria-label={t("close")}/>

            <Stack alignItems={"center"} gap={1} paddingRight={3}>
                <FullLogoSmall/>
                <Typography level={"body-sm"} textAlign={"center"} textColor={"text.tertiary"}>
                    {reason ? t(reason) : t("authDialogDefaultReason")}
                </Typography>
            </Stack>

            {/*
              * Remounted on every opening so a previous attempt never leaves its errors behind.
              * Closing is left to the session landing rather than to the form returning: a sign in
              * whose account lookup fails has to stay open and say so.
              */}
            {open && <AuthForm initialMode={initialMode}/>}
        </ModalDialog>
    </Modal>
}

export default AuthDialog
